import { 
  Component, 
  Input, 
  OnChanges, 
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  DestroyRef,
  inject,
} from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { PaButtonComponent } from '../pa-button/pa-button.component';
import { CommonModule } from '@angular/common';
import { ImagePreviewModalComponent } from '../../modals/image-preview-modal/image-preview-modal.component';
import { ModalController } from '@ionic/angular/standalone';
import { downloadBlobAsFile, getFileIcon } from '../../helpers/file-utils';
import { FileFromServer } from 'src/app/core/models/files';
import { FilesService } from 'src/app/core/services/files.service';
import { PaContextMenuService } from '../pa-context-menu/pa-context-menu.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'pa-file-list',
  standalone: true,
  imports: [Menu, PaButtonComponent, CommonModule],
  templateUrl: './pa-file-list.component.html',
  styleUrls: ['./pa-file-list.component.scss'],
})
export class PaFileListComponent 
  implements OnChanges, AfterViewInit, OnDestroy {
  @Input() files: FileFromServer[] = [];
  @Input() buttonLabel: string = 'Показать файлы';
  @Input() buttonIcon: string = 'pi pi-chevron-down';
  @Input() buttonSeverity: 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'danger' | 'help' = 'secondary';

  @ViewChild('menu') menu: Menu;

  menuItems: MenuItem[] = [];

  private readonly imgArr = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'avif'];
  private readonly destroy = inject(DestroyRef);

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly filesService: FilesService,
    private readonly contextMenuService: PaContextMenuService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['files']) {
      this.updateMenuItems();
    }
  }

  ngAfterViewInit(): void {
    if (this.menu) {
      this.contextMenuService.registerMenu(this.menu);
    }
  }

  ngOnDestroy(): void {
    if (this.menu) {
      this.contextMenuService.unregisterMenu(this.menu);
    }
  }

  handleButtonClick(event: Event): void {
    event.stopPropagation();
    this.contextMenuService.closeAllExcept(this.menu);
    this.menu.toggle(event);
  }

  private updateMenuItems(): void {
    this.menuItems = this.files.map((file) => ({
      label: file.original_name ? `${file.original_name}.${file.ext}` : 'Файл',
      icon: getFileIcon(file.ext),
      command: () => (this.isImage(file.ext) ? this.showImage(file.url) : this.downloadFile(file)),
      data: file,
    }));
  }

  downloadFile(file: FileFromServer): void {
    const fileName = `${file.original_name}.${file.ext}`;
    this.filesService
      .downloadFile(file.uuid)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: (blob: Blob) => downloadBlobAsFile(blob, fileName),
        error: () => undefined,
      });
  }

  async showImage(image: string): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ImagePreviewModalComponent,
      componentProps: { image },
      cssClass: 'image-preview-modal auto-height',
    });
    modal.present();
  }

  isImage(ext: string): boolean {
    return ext ? this.imgArr.includes(ext) : false;
  }
}
